import { useState, useEffect, useRef } from "react";
import { Line, Bar } from "react-chartjs-2";
import moment from "moment";
import colorMapping from "./colorMapping";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { FaAngleLeft, FaAngleRight } from "react-icons/fa";
import "./ticker.css";
import About from './About.js';
import uparrow from "../../assets/uparrow.png";
import search from "../../assets/search.svg";
import downarrow from "../../assets/downarrow.png";
import { useSelector, useDispatch } from "react-redux";
import { fetchStockDetails } from "../../redux/slice/tickerSlice";
import {
  addToWatchlist,
  deleteWatchlistItem,
  fetchWatchlist,
} from "../../redux/slice/watchlistSlice";
import { toast } from "react-toastify";
import tickers from "../../assets/nasdaq_tickers.json"; 

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const Showticker = () => {
  const dispatch = useDispatch();
  const symbol = useSelector((state) => state.ticker.symbol);
  const stockDetails = useSelector((state) => state.ticker.stock);
  const historicalD = useSelector((state) => state.ticker.historical_data);
  const { income_statement, balance_sheet } = useSelector(
    (state) => state.ticker.financial_statements
  );
  const watchlist = useSelector((state) => state?.watchlist?.stocks);
  const { loading, error } = useSelector((state) => state.ticker);
  const [ticker, setTicker] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [period, setPeriod] = useState("1mo");
  const [latestPrice, setLatestPrice] = useState(null);
  const [latestDate, setLatestDate] = useState(null);
  const [dayDifference, setDayDifference] = useState(null);
  const [dayPercentage, setDayPercentage] = useState(null);
  const indicators = [
    "Current Ratio",
    "Debt Ratio",
    "Quick Ratio",
    "On-Hand Cash",
  ];
  const [selectedIndicator, setSelectedIndicator] = useState("Current Ratio");
  const [calculatedRatios, setCalculatedRatios] = useState([]);
  const chartRef = useRef(null);
  const user = useSelector((state) => state.auth.user);
  const [present, setPresent] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef();

  useEffect(() => {
    if (historicalD && historicalD.length > 0) {
      filterDataByPeriod();
      const roundedPrice = historicalD[historicalD.length - 1].close.toFixed(2);
      setLatestPrice(roundedPrice);
      setLatestDate(historicalD[historicalD.length - 1].date);
      const rdd = (
        historicalD[historicalD.length - 1].close -
        historicalD[historicalD.length - 2].close
      ).toFixed(2);
      setDayDifference(rdd);
      const dpercentage = (
        (rdd / historicalD[historicalD.length - 2].close) *
        100
      ).toFixed(2);
      setDayPercentage(dpercentage);
    }
    // eslint-disable-next-line
  }, [period, historicalD]);

  useEffect(() => {
    if (symbol && watchlist.length > 0) {
      const exists = watchlist.some((item) => item.ticker === symbol);
      setPresent(exists);
    }
  }, [symbol, watchlist]);

  useEffect(() => {
    if (ticker.trim() === "") {
      setSuggestions([]);
      return;
    }

    const filtered = tickers.filter((t) =>
      t.startsWith(ticker.toUpperCase())
    );

    setSuggestions(filtered.length > 0 ? filtered : ["NO_MATCH"]);
  }, [ticker]);

  useEffect(() => {
    if (balance_sheet && balance_sheet.length > 0) {
      const ratios = balance_sheet.map((item) => {
        const {
          calendarYear,
          totalAssets,
          totalLiabilities,
          totalCurrentAssets,
          totalCurrentLiabilities,
          inventory,
          totalDebt,
          cashAndCashEquivalents,
        } = item;
        const currentRatio = totalAssets / totalLiabilities;
        const quickRatio =
          (totalCurrentAssets - inventory) / totalCurrentLiabilities;
        const debtRatio = totalDebt / totalAssets;
        const onHandCash = cashAndCashEquivalents;

        return {
          calendarYear,
          currentRatio,
          quickRatio,
          debtRatio,
          onHandCash,
        };
      });

      setCalculatedRatios(ratios.reverse());
    }
    // eslint-disable-next-line
  }, [balance_sheet]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tickers.includes(ticker.toUpperCase())) {
      toast.error("Invalid ticker. Please select a valid stock symbol.");
      return;
    }
    setPresent(false);
    try {
      dispatch(fetchStockDetails(ticker));
    } catch (error) {
      console.error("Error fetching price:", error);
    }
    setShowSuggestions(false);
    inputRef.current.blur();
    setTicker("");
    setSuggestions([]);
  };

  const handleSelect = (value) => {
    if (value === "NO_MATCH") return;
    setTicker(value);
    setShowSuggestions(false);
  };

  const handleWatchlistSubmit = async () => {
    try {
      if (!present) {
        const resultAction = await dispatch(addToWatchlist(symbol));
        if (addToWatchlist.fulfilled.match(resultAction)) {
          setPresent(true);
          toast.success("Added to Watchlist!");
        }
      } else {
        await dispatch(deleteWatchlistItem(symbol));
        setPresent(false);
        toast.success("Removed from Watchlist!");
      }
      dispatch(fetchWatchlist());
    } catch (error) {
      console.error(error);
    }
  };

  const filterDataByPeriod = () => {
    const endDate = new Date(historicalD[historicalD.length - 1].date);
    let startDate;
    switch (period) {
      case "1wk":
        startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - 7);
        break;
      case "1mo":
        startDate = new Date(endDate);
        startDate.setMonth(endDate.getMonth() - 1);
        break;
      case "3mo":
        startDate = new Date(endDate);
        startDate.setMonth(endDate.getMonth() - 3);
        break;
      case "6mo":
        startDate = new Date(endDate);
        startDate.setMonth(endDate.getMonth() - 6);
        break;
      case "1y":
        startDate = new Date(endDate);
        startDate.setFullYear(endDate.getFullYear() - 1);
        break;
      case "5y":
        startDate = new Date(endDate);
        startDate.setFullYear(endDate.getFullYear() - 5);
        break;
      case "max":
        startDate = new Date(historicalD[0].date);
        break;
      default:
        startDate = new Date(endDate);
    }
    const filtered = historicalD.filter((data) => {
      const dataDate = new Date(data.date);
      return dataDate >= startDate && dataDate <= endDate;
    });
    setFilteredData(filtered);
  };

  const MetricChartData = (data, label) => {
    const sortedData = [...income_statement].reverse();
    return {
      labels: sortedData.map((item) => item.calendarYear),
      datasets: [
        {
          label: label,
          data: sortedData.map((item) => item[data]),
          backgroundColor:
            colorMapping[data]?.backgroundColor || "rgba(0, 0, 0, 0.5)",
          borderColor: colorMapping[data]?.borderColor || "rgba(0, 0, 0, 1)",
          borderWidth: 1,
          barThickness: 25,
          yAxisID: "y1",
        },
      ],
    };
  };

  const MetricChartData2 = (data, label) => {
    const sortedData = [...balance_sheet].reverse();
    return {
      labels: sortedData.map((item) => item.calendarYear),
      datasets: [
        {
          label: label,
          data: sortedData.map((item) => item[data]),
          backgroundColor:
            colorMapping[data]?.backgroundColor || "rgba(0, 0, 0, 0.5)",
          borderColor: colorMapping[data]?.borderColor || "rgba(0, 0, 0, 1)",
          borderWidth: 1,
          barThickness: 25,
          yAxisID: "y1",
        },
      ],
    };
  };

  const MetricChartData3 = {
    labels: calculatedRatios.map((ratio) => ratio.calendarYear),
    datasets: [
      {
        label: selectedIndicator,
        data: calculatedRatios.map((ratio) => {
          if (selectedIndicator === "Current Ratio") return ratio.currentRatio;
          if (selectedIndicator === "Quick Ratio") return ratio.quickRatio;
          if (selectedIndicator === "Debt Ratio") return ratio.debtRatio;
          if (selectedIndicator === "On-Hand Cash") return ratio.onHandCash;
          return null;
        }),
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx } = chart;
          const gradient = ctx.createLinearGradient(0, 0, 0, chart.height);
          gradient.addColorStop(0, "rgba(255, 105, 180, 1)");
          gradient.addColorStop(1, "rgba(30, 144, 255, 1)");
          return gradient;
        },
        barThickness: 25,
      },
    ],
  };

  const MetricChartOptions = (data) => ({
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#333333",
        titleColor: "rgba(256, 256, 256)",
        bodyColor: "rgba(256, 256, 256)",
        borderColor: "rgba(256, 256, 256, 0.5)",
        borderWidth: 0,
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Year", color: "#ffffff" },
        grid: { display: false },
        ticks: { color: "#ffffff" },
      },
      y1: {
        title: { display: false },
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return data === "EPS" ? value : `${(value / 1e9).toFixed(0)}`;
          },
          color: "#ffffff",
        },
        grid: { display: false },
      },
    },
  });

  const formatLabelByPeriod = (date) => {
    if (period === "5y" || period === "max") {
      return moment(date).format("YYYY");
    } else if (period === "1y") {
      return moment(date).format("MMM, YY");
    } else {
      return moment(date).format("DD MMM, YY");
    }
  };

  const tickerchartData = {
    labels: filteredData.map((data) => formatLabelByPeriod(data.date)),
    datasets: [
      {
        label: "Closing Price",
        data: filteredData.map((data) => data.close),
        fill: true,
        borderColor: "rgba(0, 255, 127, 1)",
        backgroundColor: "rgba(0, 255, 127, 0.05)",
        borderWidth: 1,
        pointRadius: 0,
      },
    ],
  };

  const tickerchartOptions = {
    responsive: true,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: `${symbol} Closing Prices`,
        color: "#ffffff",
        font: { size: 17 },
      },
      tooltip: {
        enabled: true,
        padding: 10,
        callbacks: {
          label: function (tooltipItem) {
            return `$${tooltipItem.formattedValue}`;
          },
        },
        displayColors: false,
        backgroundColor: "rgb(51, 51, 51, 0.7)",
        titleColor: "rgb(256, 256, 256)",
        bodyColor: "rgb(256, 256, 256)",
        borderColor: "rgb(256, 256, 256, 0.5)",
        borderWidth: 0,
        bodyFont: {
          size: 16,
          weight: "bold",
        },
        titleFont: {
          size: 13,
          weight: "semibold",
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Date", color: "#ffffff" },
        ticks: {
          color: "#ffffff",
          maxTicksLimit: 5,
        },
      },
      y: {
        title: { display: true, text: "Price", color: "#ffffff" },
        beginAtZero: false,
        ticks: { color: "#ffffff", maxTicksLimit: 5 },
        grid: { color: "#444444" },
      },
    },
  };

  const MetricChartOptions3 = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#333333",
        titleColor: "rgb(256, 256, 256)",
        bodyColor: "rgb(256, 256, 256)",
        borderColor: "rgb(256, 256, 256, 0.5)",
        borderWidth: 0,
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Year", color: "#ffffff" },
        ticks: { color: "#ffffff" },
      },
      y: {
        title: { display: false },
        grid: { display: false },
        ticks: {
          color: "#ffffff",
          callback: function (value) {
            if (selectedIndicator === "On-Hand Cash") {
              if (value >= 1e9) {
                return (value / 1e9).toFixed(1) + "B";
              }
              if (value >= 1e6) {
                return (value / 1e6).toFixed(1) + "M";
              }
            }
            return value;
          },
        },
      },
    },
  };

  const handleLeftClick = () => {
    const currentIndex = indicators.indexOf(selectedIndicator);
    const newIndex = (currentIndex - 1 + indicators.length) % indicators.length;
    setSelectedIndicator(indicators[newIndex]);
  };

  const handleRightClick = () => {
    const currentIndex = indicators.indexOf(selectedIndicator);
    const newIndex = (currentIndex + 1) % indicators.length;
    setSelectedIndicator(indicators[newIndex]);
  };

  return (
    <div className="ticker_page">
      {historicalD.length > 0 ? (
        <form className="ticker_form mt-4" onSubmit={handleSubmit}>
          <div className="form-container-nl relative">
            <input
              ref={inputRef}
              type="text"
              value={ticker}
              onChange={(e) => setTicker(e.target.value.toUpperCase())}
              onFocus={() => setShowSuggestions(true)}
              placeholder={"Enter Ticker"}
              className="ticker-input"
            />
            <button type="submit" className="ticker-button">
              Get Price
            </button>

            {/* Suggestions Dropdown */}
            {showSuggestions && ticker && (
              <div className="suggestions-container">
                {suggestions.map((s, idx) => (
                  <div
                    key={idx}
                    className={`suggestion-item ${
                      s === "NO_MATCH" ? "no-match" : ""
                    }`}
                    onClick={() => handleSelect(s)}
                  >
                    {s === "NO_MATCH" ? "No Ticker Found" : s}
                  </div>
                ))}
              </div>
            )}
          </div>
        </form>
      ) : (
        <div className="landing-wrapper">
          <h1 className="landing-title">
            Stockasti<span style={{ color: "var(--green)" }}>x</span>
          </h1>
          <p className="landing-subtitle">Start discovering stocks</p>
          <form className="landing-form" onSubmit={handleSubmit}>
            <div className="landing-input-container relative">
              <img
                src={search}
                alt="Search Icon"
                className="search-icon"
                height={20}
                width={20}
              />
              <input
                ref={inputRef}
                type="text"
                value={ticker}
                onChange={(e) => setTicker(e.target.value.toUpperCase())}
                onFocus={() => setShowSuggestions(true)}
                placeholder="Search for a company"
                className="landing-input"
              />

              {/* Suggestions Dropdown */}
              {showSuggestions && ticker && (
                <div className="suggestions-container">
                  {suggestions.map((s, idx) => (
                    <div
                      key={idx}
                      className={`suggestion-item ${
                        s === "NO_MATCH" ? "no-match" : ""
                      }`}
                      onClick={() => handleSelect(s)}
                    >
                      {s === "NO_MATCH" ? "No Ticker Found" : s}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button type="submit" className="ticker-button">
              Get Price
            </button>
          </form>
        </div>
      )}

      {loading && (
        <div className="loader-overlay">
          <div className="loader"></div>
        </div>
      )}

      {historicalD.length > 0 && !loading && (
        <div>
          <div className="itsmain d-flex justify-content-around">
            <div className="graph_and_opts">
              <div className="opo mt-4">
                <div className="opts0">
                  <div className="details">
                    <b>Latest Price: </b>${latestPrice}
                    <p style={{ fontSize: "13px" }}>As of {latestDate}</p>
                  </div>
                  <div className="details">
                    <div
                      style={
                        dayDifference > 0
                          ? { color: "#00ae00" }
                          : { color: "#ff0f0f" }
                      }
                    >
                      <b>
                        {dayDifference > 0
                          ? `+$${dayDifference}`
                          : `-$${Math.abs(dayDifference)}`}
                      </b>
                    </div>
                    <p style={{ fontSize: "12px" }}>({dayPercentage}%)</p>
                  </div>
                  <div>
                    <img
                      className="blink"
                      src={dayDifference > 0 ? uparrow : downarrow}
                      height={30}
                      width={24}
                      alt="arrow"
                    />
                  </div>
                </div>
                <div className="opts1">
                  {["1wk", "1mo", "3mo", "6mo", "1y", "5y", "max"].map(
                    (option, index) => (
                      <span
                        key={option}
                        onClick={() => setPeriod(option)}
                        style={{
                          backgroundColor:
                            period === option ? "#ff9800" : "#333",
                          color: period === option ? "#000" : "#fff",
                        }}
                      >
                        {option}
                      </span>
                    )
                  )}
                </div>
              </div>
              <div className="main_graph mt-4">
                <Line
                  data={tickerchartData}
                  options={tickerchartOptions}
                  height={120}
                />
              </div>
            </div>
            <About stockDetails={stockDetails}/>
          </div>

          {user && (
            <div className="wlbuttondiv mt-3">
              <button className="wlbutton" onClick={handleWatchlistSubmit}>
                {present ? "- Remove from Watchlist" : "+ Add to Watchlist"}
              </button>
            </div>
          )}

          <hr />

          {income_statement.length > 0 && balance_sheet.length > 0 && (
            <>
              <div className="isgraphs">
                <div className="isgraph_data">
                  <h6>Revenue</h6>
                  <div style={{ fontSize: 14 }}>($B)</div>
                  <Bar
                    data={MetricChartData("revenue", "Revenue")}
                    options={MetricChartOptions("Revenue")}
                  />{" "}
                </div>
                <div className="isgraph_data">
                  <h6>Net Income</h6>
                  <div style={{ fontSize: 14 }}>($B)</div>
                  <Bar
                    data={MetricChartData("netIncome", "Net Income")}
                    options={MetricChartOptions("Net Income")}
                  />{" "}
                </div>
                <div className="isgraph_data">
                  <h6>Gross Profit</h6>
                  <div style={{ fontSize: 14 }}>($B)</div>
                  <Bar
                    data={MetricChartData("grossProfit", "Gross Profit")}
                    options={MetricChartOptions("Gross Profit")}
                  />{" "}
                </div>
              </div>
              <div className="isgraphs">
                <div className="isgraph_data">
                  <h6>EPS</h6>
                  <Bar
                    data={MetricChartData("eps", "EPS")}
                    options={MetricChartOptions("EPS")}
                  />{" "}
                </div>
                <div className="isgraph_data">
                  <h6>Total Assets</h6>
                  <div style={{ fontSize: 14 }}>($B)</div>
                  <Bar
                    data={MetricChartData2("totalAssets", "Total Assets")}
                    options={MetricChartOptions("Total Assets")}
                  />{" "}
                </div>
                <div className="isgraph_data">
                  <h6>Total Liabilities</h6>
                  <div style={{ fontSize: 14 }}>($B)</div>
                  <Bar
                    data={MetricChartData2(
                      "totalLiabilities",
                      "Total Liabilities"
                    )}
                    options={MetricChartOptions("Total Liabilities")}
                  />{" "}
                </div>
              </div>
              <div className="isgraphs">
                <div className="isgraph_data">
                  <h6>Total Equity</h6>
                  <div style={{ fontSize: 14 }}>($B)</div>
                  <Bar
                    data={MetricChartData2("totalEquity", "Total Equity")}
                    options={MetricChartOptions("Total Equity")}
                  />{" "}
                </div>
                <div className="isgraph_data">
                  <h6>Total Debt</h6>
                  <div style={{ fontSize: 14 }}>($B)</div>
                  <Bar
                    data={MetricChartData2("totalDebt", "Total Debt")}
                    options={MetricChartOptions("Total Debt")}
                  />{" "}
                </div>
                <div className="isgraph_data">
                  <div className="indicators">
                    <div
                      style={{ cursor: "pointer" }}
                      onClick={handleLeftClick}
                    >
                      <FaAngleLeft />
                    </div>
                    <div className="ind_name">{selectedIndicator}</div>
                    <div
                      style={{ cursor: "pointer" }}
                      onClick={handleRightClick}
                    >
                      <FaAngleRight />
                    </div>
                  </div>
                  <Bar
                    data={MetricChartData3}
                    options={MetricChartOptions3}
                    ref={chartRef}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Showticker;
